import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, TrendingUp, Diamond, Sparkles, Car, Watch, Home, Gift, ChevronRight, X, Check, Star, Gem, Lock, ArrowLeftRight, Loader2, RefreshCw, Zap, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

import { useCurrency } from '@/context/CurrencyContext';
import PriceDisplay from '@/components/PriceDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = { 'crown': Crown, 'trending-up': TrendingUp, 'diamond': Diamond, 'sparkles': Sparkles };
const itemTypeIcons = { property: Home, car: Car, watch: Watch, accessory: Gift };
const itemTypeLabels = { property: 'Property', car: 'Car', watch: 'Timepiece', accessory: 'Accessory' };

const LifestyleBundles = ({ tier = 'free', isAdmin = false, onboardingComplete = false }) => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [bundles, setBundles] = useState([]);
  const [lockedBundles, setLockedBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [profileComplete, setProfileComplete] = useState(false);
  const [preferenceSummary, setPreferenceSummary] = useState(null);
  const [maxBundles, setMaxBundles] = useState(3);
  const canSwap = isAdmin || tier === 'premium' || tier === 'elite';

  // Swap state
  const [swapMode, setSwapMode] = useState(null); // { bundleId, slotIndex, assetType }
  const [swapOptions, setSwapOptions] = useState([]);
  const [swapLoading, setSwapLoading] = useState(false);
  const [swapResult, setSwapResult] = useState(null);
  const [noMatch, setNoMatch] = useState(null);


  const fetchBundles = async () => {
    try {
      const response = await axios.get(`${API}/bundles`);
      const d = response.data;
      setBundles(d.bundles || []);
      setLockedBundles(d.locked_bundles || []);
      setProfileComplete(d.profile_complete);
      setPreferenceSummary(d.preferences_summary);
      setMaxBundles(d.max_bundles || 3);
      setNoMatch(d.no_match || null);
    } catch (error) {
      // Fallback to old endpoint
      try {
        const response = await axios.get(`${API}/lifestyle-bundles`);
        setBundles(response.data.bundles || []);
        setProfileComplete(response.data.profile_complete);
        setPreferenceSummary(response.data.preferences_summary);
      } catch (e2) {
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBundles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingComplete, tier]);

  const trackAction = async (action, itemId, itemType, category, bundleId, metadata) => {
    try {
      await axios.post(`${API}/bundles/track`, { action, item_id: itemId, item_type: itemType, category, bundle_id: bundleId, metadata });
    } catch (e) { /* silent */ }
  };

  const handleSwapStart = async (bundleId, slotIndex, assetType) => {
    if (!canSwap) {
      toast.info('Upgrade to Premium to swap assets in bundles');
      navigate('/subscription');
      return;
    }
    setSwapMode({ bundleId, slotIndex, assetType });
    setSwapLoading(true);
    setSwapResult(null);
    try {
      const res = await axios.get(`${API}/bundles/swap-options`, { params: { asset_type: assetType, bundle_id: bundleId } });
      setSwapOptions(res.data.options || []);
    } catch (e) {
      toast.error('Failed to load swap options');
      setSwapMode(null);
    } finally {
      setSwapLoading(false);
    }
  };

  const handleSwapConfirm = async (newAssetId) => {
    if (!swapMode) return;
    setSwapLoading(true);
    try {
      const bundle = bundles.find(b => b.id === swapMode.bundleId);
      const oldItem = bundle?.items?.[swapMode.slotIndex];
      const res = await axios.post(`${API}/bundles/swap`, {
        bundle_id: swapMode.bundleId,
        asset_type: swapMode.assetType,
        old_asset_id: oldItem?.item?.id,
        new_asset_id: newAssetId,
      });
      setSwapResult(res.data);
      // Update bundle locally
      const newAsset = res.data.new_asset;
      const updatedBundles = bundles.map(b => {
        if (b.id !== swapMode.bundleId) return b;
        const newItems = [...b.items];
        newItems[swapMode.slotIndex] = { type: swapMode.assetType, item: newAsset };
        const tv = newItems.reduce((s, i) => s + (i.item?.price || 0), 0);
        return { ...b, items: newItems, total_value: tv, match_score: res.data.scores.overall };
      });
      setBundles(updatedBundles);
      trackAction('swap', newAssetId, swapMode.assetType, swapMode.assetType, swapMode.bundleId, {});
      toast.success('Asset swapped! Scores updated.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Swap failed');
    } finally {
      setSwapLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="glass-card p-12 text-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 bg-gold/20 rounded-full" />
          <div className="h-4 bg-white/10 rounded w-48" />
        </div>
      </div>
    );
  }

  if (!profileComplete) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-12 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-purple-500/5" />
        <div className="relative z-10">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gold/20 flex items-center justify-center">
            <Gem className="w-10 h-10 text-gold" />
          </div>
          <h3 className="font-playfair text-3xl font-semibold mb-4">Unlock Your Smart Plan</h3>
          <p className="text-text-secondary text-lg mb-8 max-w-xl mx-auto">Complete your profile to receive personalized, goal-based recommendations curated just for you.</p>
          <Button onClick={() => navigate('/onboarding')} className="bg-gold text-black hover:bg-gold-light px-8 py-6 text-lg" data-testid="complete-profile-for-bundles">
            <Sparkles className="mr-2 h-5 w-5" /> Complete Profile
          </Button>
        </div>
      </motion.div>
    );
  }

  if (noMatch && bundles.length === 0) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-12 text-center relative overflow-hidden" data-testid="no-match-message">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-red-500/5" />
        <div className="relative z-10">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-amber-500/20 flex items-center justify-center">
            <Search className="w-10 h-10 text-amber-400" />
          </div>
          <h3 className="font-playfair text-2xl font-semibold mb-4">No Matching Assets Found</h3>
          <p className="text-text-secondary text-lg mb-4 max-w-xl mx-auto">{noMatch}</p>
          {preferenceSummary && (
            <div className="flex flex-wrap gap-3 justify-center mb-8 text-sm">
              <span className="px-3 py-1 bg-white/5 rounded-full text-text-secondary">Property: {preferenceSummary.budget_range}</span>
              {preferenceSummary.car_budget && preferenceSummary.car_budget !== 'Not specified' && <span className="px-3 py-1 bg-white/5 rounded-full text-text-secondary">Car: {preferenceSummary.car_budget}</span>}
              {preferenceSummary.bedrooms && preferenceSummary.bedrooms !== 'Any' && <span className="px-3 py-1 bg-white/5 rounded-full text-text-secondary">{preferenceSummary.bedrooms} Bedrooms</span>}
              {preferenceSummary.bathrooms && preferenceSummary.bathrooms !== 'Any' && <span className="px-3 py-1 bg-white/5 rounded-full text-text-secondary">{preferenceSummary.bathrooms} Bathrooms</span>}
            </div>
          )}
          <Button onClick={() => navigate('/account')} className="bg-gold text-black hover:bg-gold-light px-8 py-6 text-lg" data-testid="adjust-preferences-btn">
            Adjust Preferences
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-8" data-testid="lifestyle-bundles-section">
      {/* Aspirational banner — shown when we couldn't find exact matches */}
      {noMatch && bundles.length > 0 && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="glass-card p-4 border border-amber-500/30 bg-amber-500/5 flex items-start gap-3"
          data-testid="aspirational-notice">
          <Search className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-amber-200">{noMatch}</p>
            <button onClick={() => navigate('/account')} className="text-xs text-gold hover:underline mt-1" data-testid="adjust-preferences-inline">
              Adjust preferences →
            </button>
          </div>
        </motion.div>
      )}

      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-playfair text-3xl font-semibold flex items-center gap-3">
            <Diamond className="w-8 h-8 text-gold" />
            AI Smart Plans
          </h3>
          <p className="text-text-secondary mt-2">
            {canSwap ? 'Goal-based recommendations — swap any asset to customize' : 'Smart plans matching your goals and budget'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {preferenceSummary && (
            <span className="hidden md:inline-block px-3 py-1 bg-white/5 rounded-full text-sm text-text-secondary">{preferenceSummary.budget_range}</span>
          )}
          {maxBundles > 0 && !isAdmin && tier === 'free' && (
            <span className="px-3 py-1 bg-gold/10 text-gold rounded-full text-xs font-medium" data-testid="bundle-limit-badge">
              {bundles.length}/{maxBundles} Free Bundles
            </span>
          )}
        </div>
      </div>

      {/* Bundle Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" style={{ perspective: '1500px' }}>
        {bundles.map((bundle, index) => {
          const IconComponent = iconMap[bundle.icon] || Crown;
          const isFeatured = bundle.featured;
          return (
            <motion.div key={bundle.id} initial={{ opacity: 0, rotateY: -15, z: -100 }} animate={{ opacity: 1, rotateY: 0, z: 0 }}
              transition={{ delay: index * 0.15, duration: 0.6, type: 'spring' }}
              whileHover={{ scale: 1.02, rotateY: 5, z: 50, transition: { duration: 0.3 } }}
              className={`relative group cursor-pointer ${isFeatured ? 'lg:-mt-4 lg:mb-4' : ''}`}
              style={{ transformStyle: 'preserve-3d' }}
              onClick={() => { setSelectedBundle(bundle); trackAction('click', bundle.id, 'bundle', 'bundles', bundle.id); }}
              data-testid={`bundle-card-${bundle.id}`}
            >
              {isFeatured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                  <div className="bg-gradient-to-r from-gold to-amber-500 text-black px-6 py-2 rounded-full text-sm font-bold flex items-center gap-2 shadow-lg shadow-gold/30">
                    <Star className="w-4 h-4" /> BEST MATCH
                  </div>
                </div>
              )}
              <div className={`relative overflow-hidden rounded-2xl border transition-all duration-500 ${isFeatured ? 'border-gold/50 shadow-2xl shadow-gold/20' : 'border-white/10 hover:border-white/30'}`}
                style={{ background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0.3) 100%)', backdropFilter: 'blur(20px)' }}>
                {/* Header */}
                <div className={`h-32 bg-gradient-to-r ${bundle.gradient} relative overflow-hidden`}>
                  <div className="absolute inset-0 bg-black/20" />
                  <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <motion.div animate={{ rotateY: [0, 360] }} transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                      className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center" style={{ transformStyle: 'preserve-3d' }}>
                      <IconComponent className="w-8 h-8 text-white" />
                    </motion.div>
                  </div>
                  <div className="absolute top-4 right-4 bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="text-white text-sm font-bold">{bundle.match_score}% Match</span>
                  </div>
                  {bundle.risk_level && (
                    <div className="absolute bottom-4 right-4 bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full">
                      <span className={`text-xs font-medium ${bundle.risk_level === 'conservative' ? 'text-emerald-300' : bundle.risk_level === 'aggressive' ? 'text-rose-300' : 'text-amber-300'}`}>
                        {bundle.risk_level.charAt(0).toUpperCase() + bundle.risk_level.slice(1)} Risk
                      </span>
                    </div>
                  )}
                </div>
                {/* Content */}
                <div className="p-6">
                  <h4 className="font-playfair text-2xl font-semibold mb-1">{bundle.name}</h4>
                  <p className="text-gold text-sm mb-2">{bundle.tagline}</p>
                  {bundle.expected_return && (
                    <p className="text-emerald-400 text-xs mb-4 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" /> {bundle.expected_return}
                    </p>
                  )}
                  <div className="grid grid-cols-4 gap-2 mb-6">
                    {bundle.items.slice(0, 4).map((item, idx) => {
                      const ItemIcon = itemTypeIcons[item.type] || Gift;
                      return (
                        <motion.div key={`tag-${idx}`} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3 + idx * 0.1 }}
                          className="aspect-square rounded-lg overflow-hidden relative group/item">
                          <img src={item.item?.images?.[0] || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=200'} alt={item.item?.title || item.type}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover/item:scale-110" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover/item:opacity-100 transition-opacity flex items-end justify-center p-1">
                            <ItemIcon className="w-4 h-4 text-white" />
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <div>
                      <p className="text-text-secondary text-xs">Total Value</p>
                      <PriceDisplay amount={bundle.total_value} className="text-2xl font-bold text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-emerald-400 text-sm font-medium">Bundle Savings</p>
                      <p className="text-emerald-400 font-bold"><PriceDisplay amount={bundle.savings} className="text-emerald-400 font-bold" /></p>
                    </div>
                  </div>
                  <Button className={`w-full mt-6 ${isFeatured ? 'bg-gradient-to-r from-gold to-amber-500 text-black hover:from-gold-light hover:to-amber-400' : 'bg-white/10 hover:bg-white/20 text-white'}`}
                    data-testid={`view-bundle-${bundle.id}`}>
                    View Collection <ChevronRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          );
        })}

        {/* Locked bundle teasers */}
        {lockedBundles.map((lb, index) => {
          const IconComponent = iconMap[lb.icon] || Crown;
          return (
            <motion.div key={`locked-${lb.id}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (bundles.length + index) * 0.15 }}
              className="relative group cursor-pointer" data-testid={`locked-bundle-${lb.id}`}
              onClick={() => { toast.info('Upgrade to Premium to unlock all AI bundles'); navigate('/subscription'); }}
            >
              <div className="relative overflow-hidden rounded-2xl border border-white/5 transition-all duration-500 hover:border-gold/20"
                style={{ background: 'linear-gradient(145deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 100%)', backdropFilter: 'blur(20px)' }}>
                <div className={`h-32 bg-gradient-to-r ${lb.gradient} relative overflow-hidden`}>
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                    <Lock className="w-10 h-10 text-white/60" />
                  </div>
                </div>
                <div className="p-6 opacity-60">
                  <h4 className="font-playfair text-2xl font-semibold mb-1">{lb.name}</h4>
                  <p className="text-gold text-sm mb-4">{lb.tagline}</p>
                  <div className="grid grid-cols-4 gap-2 mb-6">
                    {lb.items.slice(0, 2).map((item, idx) => (
                      <div key={`gallery-${idx}`} className="aspect-square rounded-lg overflow-hidden relative bg-white/5">
                        <img src={item.item?.images?.[0] || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=200'} alt=""
                          className="w-full h-full object-cover blur-sm" />
                      </div>
                    ))}
                    <div className="aspect-square rounded-lg bg-white/5 flex items-center justify-center col-span-2">
                      <Lock className="w-6 h-6 text-white/30" />
                    </div>
                  </div>
                  <Button className="w-full mt-2 bg-gold/20 text-gold hover:bg-gold/30 border border-gold/30">
                    <Crown className="mr-2 w-4 h-4" /> Unlock with Premium
                  </Button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Upgrade CTA for free tier with locked bundles */}
      {lockedBundles.length > 0 && tier === 'free' && !isAdmin && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="glass-card p-6 border border-gold/20 bg-gradient-to-r from-gold/5 to-purple-500/5" data-testid="bundles-upgrade-cta">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gold/20 flex items-center justify-center">
                <Zap className="w-6 h-6 text-gold" />
              </div>
              <div>
                <h4 className="font-semibold text-lg">Unlock {lockedBundles.length} More Smart Plan{lockedBundles.length > 1 ? 's' : ''}</h4>
                <p className="text-text-secondary text-sm">Get unlimited plans, Smart Matches, Swap Asset & more with Premium</p>
              </div>
            </div>
            <Button onClick={() => navigate('/subscription')} className="bg-gold text-black hover:bg-gold-light" data-testid="upgrade-for-bundles-btn">
              <Crown className="mr-2 w-4 h-4" /> Upgrade Now
            </Button>
          </div>
        </motion.div>
      )}

      {/* ─── Bundle Detail Modal ─── */}
      <AnimatePresence>
        {selectedBundle && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
            onClick={() => { setSelectedBundle(null); setSwapMode(null); setSwapResult(null); }}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="bg-obsidian border border-white/20 rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto overscroll-contain"
              onClick={(e) => e.stopPropagation()} data-testid="bundle-detail-modal" data-lenis-prevent>
              {/* Modal Header */}
              <div className={`h-48 bg-gradient-to-r ${selectedBundle.gradient} relative`}>
                <div className="absolute inset-0 bg-black/30" />
                <button onClick={() => { setSelectedBundle(null); setSwapMode(null); setSwapResult(null); }}
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors"
                  data-testid="close-bundle-modal"><X className="w-5 h-5 text-white" /></button>
                <div className="absolute bottom-6 left-8">
                  <div className="flex items-center gap-4 mb-2">
                    {selectedBundle.featured && <span className="bg-gold text-black px-3 py-1 rounded-full text-sm font-bold">RECOMMENDED</span>}
                    <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">{selectedBundle.match_score}% Match</span>
                    {canSwap && <span className="bg-blue-500/20 backdrop-blur-sm text-blue-300 px-3 py-1 rounded-full text-sm flex items-center gap-1"><ArrowLeftRight className="w-3 h-3" /> Swap Enabled</span>}
                  </div>
                  <h2 className="font-playfair text-4xl font-bold text-white">{selectedBundle.name}</h2>
                  <p className="text-white/80 text-lg">{selectedBundle.tagline}</p>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-8">
                <p className="text-text-secondary text-lg mb-4">{selectedBundle.description}</p>

                {/* Risk & Return Info */}
                {(selectedBundle.risk_level || selectedBundle.expected_return) && (
                  <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10 flex flex-wrap gap-6" data-testid="bundle-risk-return-info">
                    {selectedBundle.risk_level && (
                      <div>
                        <p className="text-text-secondary text-xs mb-1">Risk Level</p>
                        <span className={`text-sm font-semibold ${selectedBundle.risk_level === 'conservative' ? 'text-emerald-400' : selectedBundle.risk_level === 'aggressive' ? 'text-rose-400' : 'text-amber-400'}`}>
                          {selectedBundle.risk_level.charAt(0).toUpperCase() + selectedBundle.risk_level.slice(1)}
                        </span>
                      </div>
                    )}
                    {selectedBundle.expected_return && (
                      <div>
                        <p className="text-text-secondary text-xs mb-1">Expected Return</p>
                        <span className="text-sm font-semibold text-emerald-400">{selectedBundle.expected_return}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Swap Result Score */}
                {swapResult && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20" data-testid="swap-score-result">
                    <div className="flex items-center gap-3 mb-3">
                      <RefreshCw className="w-5 h-5 text-blue-400" />
                      <span className="font-semibold text-blue-300">Swap Compatibility Score</span>
                    </div>
                    <div className="grid grid-cols-4 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-gold">{swapResult.scores.overall}%</div>
                        <div className="text-xs text-text-secondary">Overall</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-emerald-400">{swapResult.scores.budget_compatibility}%</div>
                        <div className="text-xs text-text-secondary">Budget Fit</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-purple-400">{swapResult.scores.preference_match}%</div>
                        <div className="text-xs text-text-secondary">Preference</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-blue-400">{swapResult.scores.lifestyle_alignment}%</div>
                        <div className="text-xs text-text-secondary">Lifestyle</div>
                      </div>
                    </div>
                    {!swapResult.budget_info.within_budget && (
                      <p className="text-amber-400 text-sm mt-2 text-center">This asset is outside your ideal budget range</p>
                    )}
                  </motion.div>
                )}

                {/* Items Grid — each asset gets its OWN inline swap section */}
                <div className="space-y-6">
                  {selectedBundle.items.map((item, idx) => {
                    const ItemIcon = itemTypeIcons[item.type] || Gift;
                    const itemData = item.item;
                    const isSwapActiveHere = swapMode?.bundleId === selectedBundle.id && swapMode?.slotIndex === idx;
                    return (
                      <div key={`asset-row-${idx}`}>
                      <motion.div key={`asset-${idx}`} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.1 }}
                        className="flex gap-6 p-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors group" data-testid={`bundle-item-${item.type}`}>
                        <div className="w-48 h-32 rounded-xl overflow-hidden flex-shrink-0 relative">
                          <img src={itemData?.images?.[0] || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400'} alt={itemData?.title || item.type}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-lg flex items-center gap-1">
                            <ItemIcon className="w-3 h-3 text-gold" />
                            <span className="text-xs text-white">{itemTypeLabels[item.type]}</span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-semibold text-xl text-white">{itemData?.title || itemData?.model || 'Premium Item'}</h4>
                              <p className="text-text-secondary">{itemData?.brand && `${itemData.brand} \u2022 `}{itemData?.location || itemData?.community || ''}</p>
                            </div>
                            <div className="text-right">
                            <PriceDisplay amount={itemData?.price} className="text-gold text-2xl font-bold" />
                              {itemData?.luxury_tier && <span className="text-xs text-text-secondary">{itemData.luxury_tier} Tier</span>}
                            </div>
                          </div>
                          <div className="flex gap-4 mt-3 text-sm text-text-secondary">
                            {item.type === 'property' && (<><span>{itemData?.bedrooms} Beds</span><span>{itemData?.bathrooms} Baths</span>{itemData?.built_area && <span>{itemData.built_area.toLocaleString()} sqft</span>}</>)}
                            {item.type === 'car' && (<><span>{itemData?.year}</span><span>{itemData?.condition}</span></>)}
                            {item.type === 'watch' && (<><span>{itemData?.year}</span><span>{itemData?.condition}</span></>)}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 self-center">
                          <Button variant="outline" size="sm" className="border-gold/50 text-gold hover:bg-gold/10"
                            onClick={(e) => { e.stopPropagation(); if (item.type === 'property') navigate(`/properties/${itemData?.id}`); else navigate(`/marketplace?id=${itemData?.id}`); setSelectedBundle(null); }}>
                            View Details
                          </Button>
                          {canSwap && (
                            <Button variant="outline" size="sm" className={`border-blue-400/50 ${isSwapActiveHere ? 'bg-blue-500/20 text-blue-200' : 'text-blue-300 hover:bg-blue-500/10'}`}
                              onClick={(e) => { e.stopPropagation();
                                if (isSwapActiveHere) { setSwapMode(null); setSwapResult(null); }
                                else handleSwapStart(selectedBundle.id, idx, item.type);
                              }}
                              data-testid={`swap-btn-${item.type}-${idx}`}>
                              <ArrowLeftRight className="w-3 h-3 mr-1" /> {isSwapActiveHere ? 'Close' : 'Swipe'}
                            </Button>
                          )}
                        </div>
                      </motion.div>

                      {/* Inline per-asset swipe panel — shows directly below this item */}
                      <AnimatePresence>
                        {isSwapActiveHere && (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                            className="mt-3 overflow-hidden" data-testid={`swap-options-panel-${idx}`}>
                            <div className="p-5 rounded-2xl bg-blue-500/5 border border-blue-500/20">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-sm flex items-center gap-2">
                                  <ArrowLeftRight className="w-4 h-4 text-blue-400" />
                                  Swipe to replace this {itemTypeLabels[item.type] || item.type}
                                </h4>
                              </div>
                              {swapLoading ? (
                                <div className="flex items-center justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-blue-400" /></div>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[360px] overflow-y-auto">
                                  {swapOptions.map((opt) => (
                                    <div key={opt.id} className="p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer border border-transparent hover:border-blue-400/30 group"
                                      onClick={() => handleSwapConfirm(opt.id)} data-testid={`swap-option-${opt.id}`}>
                                      <div className="flex gap-3">
                                        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                                          <img src={opt.images?.[0] || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=200'} alt="" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <h5 className="font-medium text-sm truncate">{opt.title || opt.project_name || opt.model}</h5>
                                          <p className="text-gold text-sm font-bold">{formatPrice(opt.price || opt.price_from)}</p>
                                          <div className="flex items-center gap-2 mt-1">
                                            <div className="w-16 h-1.5 bg-white/10 rounded-full overflow-hidden">
                                              <div className="h-full bg-blue-400 rounded-full" style={{ width: `${opt.compatibility || 50}%` }} />
                                            </div>
                                            <span className="text-xs text-blue-300">{opt.compatibility || 50}%</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                  {swapOptions.length === 0 && <p className="col-span-3 text-center text-text-secondary py-4">No swipe options available for this type</p>}
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                      </div>
                    );
                  })}
                </div>

                {/* (Legacy global swap panel removed — each asset row above now has its own inline swipe panel.) */}
                {/* Summary Footer */}
                <div className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-gold/10 to-amber-500/10 border border-gold/20">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <p className="text-text-secondary mb-1">Bundle Total Value</p>
                      <PriceDisplay amount={selectedBundle.total_value} className="text-4xl font-bold text-white" />
                    </div>
                    <div className="text-center px-8 border-x border-white/10">
                      <p className="text-text-secondary mb-1">Your Savings</p>
                      <PriceDisplay amount={selectedBundle.savings} className="text-3xl font-bold text-emerald-400" />
                    </div>
                    <div>
                      <Button className="bg-gold text-black hover:bg-gold-light px-8 py-6 text-lg"
                        onClick={async () => {
                          try {
                            const bundleData = {
                              name: selectedBundle.name, theme: selectedBundle.theme, tagline: selectedBundle.tagline,
                              description: selectedBundle.description, gradient: selectedBundle.gradient, icon: selectedBundle.icon,
                              items: selectedBundle.items?.map(i => ({ type: i.type, item: { id: i.item?.id, title: i.item?.title || i.item?.model, brand: i.item?.brand, price: i.item?.price, images: i.item?.images?.slice(0, 2), location: i.item?.location, category: i.item?.category } })) || [],
                              total_value: selectedBundle.total_value, savings: selectedBundle.savings, match_score: selectedBundle.match_score,
                            };
                            await axios.post(`${API}/wishlist/add`, { item_type: 'bundle', item_id: selectedBundle.id, bundle_name: selectedBundle.name, title: selectedBundle.name, bundle_data: bundleData });
                            trackAction('save', selectedBundle.id, 'bundle', 'bundles', selectedBundle.id);
                            toast.success(`${selectedBundle.name} bundle added to your wishlist!`);
                            setSelectedBundle(null);
                          } catch (error) {
                            if (error.response?.data?.status === 'exists') toast.info('This bundle is already in your wishlist');
                            else toast.error('Failed to add bundle to wishlist');
                          }
                        }} data-testid="add-bundle-wishlist">
                        <Check className="mr-2 w-5 h-5" /> Save Bundle
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @keyframes shine { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
        [data-lenis-prevent] { overscroll-behavior: contain; -webkit-overflow-scrolling: touch; }
      `}</style>
    </div>
  );
};

export default LifestyleBundles;
